import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { ActionBar } from "../components/ActionBar";
import { I } from "../components/icons";
import { ProgramEditorSheet } from "../components/ProgramEditorSheet";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { TopBar } from "../components/TopBar";
import { programTotals } from "../domain/program";
import type { TreadmillProgram } from "../domain/types";
import { useKaya } from "../store/kayaStore";
import "./Train.css";

const blank = (): TreadmillProgram => ({
  id: "p" + Date.now().toString(36),
  name: "",
  blocks: [{ mins: 5, speed: 4, incline: 0 }],
});

/**
 * Transcribe the programs built into your own treadmill.
 *
 * Kaya cannot drive the machine, so this is not remote control — it is Kaya knowing what the
 * machine does, which is what lets a session run on P7 log its real distance instead of the
 * protocol Kaya assumed. Programs live in the user's own state, so they are private to the
 * account with no extra handling.
 */
export default function Programs() {
  const S = useKaya((s) => s.S);
  const programs = S.profile.programs || [];
  const flat = !!S.profile.inclineOverride;
  const [editing, setEditing] = useState<{ program: TreadmillProgram; isNew: boolean } | null>(null);

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="train-page">
            <TopBar title="Treadmill programs" back={{ to: "/train", label: "Back to Train" }} />

            {programs.length ? (
              <RowGroup heading="Saved">
                {programs.map((p) => {
                  const t = programTotals(S, p, flat);
                  return (
                    <NavRow
                      key={p.id}
                      icon={I.list}
                      title={p.name}
                      subtitle={`${p.blocks.length} blocks · ${Math.round(t.secs / 60)} min · ${t.km.toFixed(2)} km · ~${t.kcal} kcal est.`}
                      onClick={() => setEditing({ program: p, isNew: false })}
                    />
                  );
                })}
              </RowGroup>
            ) : null}

            <p className="train-fine">
              {programs.length
                ? "Copy the programs built into your treadmill’s console, block by block. Run one from Treadmill."
                : "Nothing yet. Most machines print the speed and incline profile for each program in the manual, or step through it on the console."}{" "}
              Once Kaya knows the speeds it can log what you actually ran, and learn your stride from
              it. These are saved to your account only.
            </p>

            <ActionBar
              primary={{
                label: "Add a program",
                icon: I.plus,
                onClick: () => setEditing({ program: blank(), isNew: true }),
              }}
            />
          </div>
        </Screen>
        {editing ? (
          <ProgramEditorSheet
            key={editing.program.id}
            program={editing.program}
            isNew={editing.isNew}
            onClose={() => setEditing(null)}
          />
        ) : null}
      </IonContent>
    </IonPage>
  );
}
